import log from "loglevel"
import { create } from "zustand"
import { API, NexusSuccessResponse } from "../api/API"
import {
    EventReviewGetDTO,
    FlightlineEventReview,
    MissionReviewType,
    flightlineEventReviewId,
} from "../models/MissionReview"
import { SortOrder } from "../models/SortOrder"
import { getEventsForMission, getFlightPlannedLines } from "../store/actions/MissionsActions"
import { FlightplanType } from "../store/types/FlightPlansTypes"
import { MissionReviewUtils } from "../utils/MissionReviewUtils"
import { getComparator } from "../utils/TableUtils"

interface ReviewStore {
    missionName?: string
    missionReview?: MissionReviewType
    flightLineReviews?: FlightlineEventReview[]
    flightPlans?: FlightplanType[]
    currentPartialLines?: FlightlineEventReview[]
    selectedEventIds: number[]
    isNodeBasedMission: boolean
    isFetching: boolean
    error?: string
}

interface ReviewStoreActions {
    setMissionName: (missionName?: string) => void
    setMissionReview: (newData: Partial<MissionReviewType>) => void
    setFlightLineReviews: (flightLineReviews?: FlightlineEventReview[]) => void
    resetReview: () => void
    setCurrentPartialLines: (rowPK?: number) => void
    updatePartialLines: (partialLines: FlightlineEventReview[]) => void
    setSelectedEventIds: (selectedIds: number[]) => void
    savePartialLinesToMissionReview: () => void
    fetchMissionReview: (missionId: string, sortOrder: SortOrder<keyof FlightlineEventReview>) => void
}

export const useMissionReviewStore = create<ReviewStore & ReviewStoreActions>((set, get) => ({
    isNodeBasedMission: false,
    selectedEventIds: [],
    isFetching: false,
    setMissionReview: (newData) => {
        const oldData = get().missionReview
        set({ missionReview: { ...oldData, ...newData } })
    },
    setFlightLineReviews: (flightLineReviews) => set({ flightLineReviews }),
    setMissionName: (missionName) => set({ missionName }),
    savePartialLinesToMissionReview: () => {
        const prevEventReviews = get().flightLineReviews ?? []

        const partialReviews = get().currentPartialLines ?? []
        const firstReview = partialReviews[0]

        // Get index of first instance where flight_line_event_id is used
        // all new reviews will have the same flight_line_event_id as they're all split from the same event
        const index = prevEventReviews.findIndex(
            (review) => review.event.flight_line_event_id === firstReview.event.flight_line_event_id,
        )
        // Filter out all usages of reviews with selected flight_line_event_id in table
        const reviewsWithoutSelectedEvent = prevEventReviews.filter(
            (review) => review.event.fe_event_id !== firstReview.event.fe_event_id,
        )

        // we need to mark all the reviews as from_split_event to true so it is saved properly on the backend
        const markedPartialReviews: FlightlineEventReview[] = partialReviews.map((review) => ({
            ...review,
            from_split_event: true,
        }))

        // insert newly created and updated partial lines where old reviews with matching event_id was
        reviewsWithoutSelectedEvent.splice(index, 0, ...markedPartialReviews)

        // save to state
        set({
            flightLineReviews: reviewsWithoutSelectedEvent,
            currentPartialLines: undefined,
        })
    },
    setCurrentPartialLines: (rowPK) => {
        if (!rowPK) {
            // Invalidate
            set({ currentPartialLines: undefined })

            return
        }

        const eventReviews = get().flightLineReviews ?? []
        const selectedReview = eventReviews.find((review) => review[flightlineEventReviewId] === rowPK)

        if (selectedReview) {
            const reviewsWithSameFlightline = eventReviews.filter(
                (review) => review.event.fe_event_id === selectedReview.event.fe_event_id,
            )
            const sortedReviews = reviewsWithSameFlightline.sort(sortByNodeNameOrDistance)

            set({ currentPartialLines: sortedReviews })
        }
    },
    updatePartialLines: (partialLines) => set({ currentPartialLines: partialLines }),
    setSelectedEventIds: (selectedEventIds: number[]) => set({ selectedEventIds }),
    resetReview: () =>
        set({
            missionReview: undefined,
            flightLineReviews: undefined,
            flightPlans: undefined,
            currentPartialLines: undefined,
            selectedEventIds: [],
            isNodeBasedMission: false,
        }),
    fetchMissionReview: async (missionId, sortOrder: SortOrder<keyof FlightlineEventReview>) => {
        set({ isFetching: true })

        try {
            const missionReviewResult: NexusSuccessResponse<{
                mission_review: MissionReviewType
                flightline_event_reviews: EventReviewGetDTO[]
            }> = await API.get(`/mission-review/${missionId}`, "Couldn't fetch mission review.")

            const eventReviews = missionReviewResult.data.flightline_event_reviews

            const { flightline_events, flight_plans } = await getEventsForMission(missionId)
            const hasNodes = flightline_events.find((event) => event.node_events?.length) != null

            const flightPlanIds: number[] = []
            flight_plans.forEach((fp) => fp.flight_plan_id && flightPlanIds.push(fp.flight_plan_id))
            const plannedLines = await getFlightPlannedLines(flightPlanIds)

            const transformedReviews: FlightlineEventReview[] = []
            let insertedId = 1
            flightline_events.forEach((event) => {
                const matchingReviews =
                    eventReviews?.filter((review) => review.flight_line_event_id === event.flight_line_event_id) ?? []
                const eventPlannedLines = plannedLines.filter((pl) =>
                    event.fe_planned_line_ids.includes(pl.planned_line_id),
                )

                if (matchingReviews.length) {
                    const matchingTransformedReviews: FlightlineEventReview[] = []
                    matchingReviews.forEach((review) => {
                        // Doesn't have node names until transformed
                        matchingTransformedReviews.push(
                            MissionReviewUtils.transformEventToReview(
                                event,
                                insertedId,
                                eventPlannedLines,
                                review,
                                matchingReviews.length > 1,
                            ),
                        )
                        insertedId++
                    })

                    // Sort the newly transformed reviews and then add the segment number
                    matchingTransformedReviews.sort(sortByNodeNameOrDistance).forEach((review, idx) => {
                        transformedReviews.push({
                            ...review,
                            segment_number: matchingReviews.length > 1 ? idx + 1 : undefined,
                        })
                    })
                } else {
                    transformedReviews.push(
                        MissionReviewUtils.transformEventToReview(event, insertedId, eventPlannedLines),
                    )
                    insertedId++
                }
            })

            set({
                isFetching: false,
                flightPlans: flight_plans,
                missionReview: missionReviewResult.data.mission_review,
                flightLineReviews: transformedReviews.sort(getComparator(sortOrder.order, sortOrder.orderBy)),
                selectedEventIds: [],
                isNodeBasedMission: hasNodes,
            })
        } catch (e) {
            const errorMessage = `Error fetching mission review of mission ${missionId}. Error was: ${e.message}`
            log.error(errorMessage, e)
            set({
                error: errorMessage,
                isFetching: false,
            })
        }
    },
}))

const sortByNodeNameOrDistance = (a: FlightlineEventReview, b: FlightlineEventReview) => {
    if (a.node_info && b.node_info) {
        return a.node_info.start_node_name > b.node_info?.start_node_name ? 1 : -1
    }
    if (a.distance_info && b.distance_info) {
        return a.distance_info.start_distance_nm > b.distance_info.start_distance_nm ? 1 : -1
    }
    return -1
}
